import { GUDHUB_SETTINGS } from '../../gudhub_settings.js';

// Direct-message notifications on the phone (gudhub-mobile's GhMessages
// plugin: ios/App/App/GhMessagesPlugin.swift, android/.../GhMessagesPlugin.java).
//
//   conversation server  pushes every new direct message to the recipient's
//                        phones as it saves it, and keeps the phones and the
//                        badge (conversation-nodejs: server/push-notification/mobile)
//   phone                native code draws, groups and de-duplicates the
//                        notifications and keeps them off the conversation on screen
//   this page            registers the phone, opens a tapped conversation, and
//                        reports what is being looked at, so that its
//                        notifications go - on this phone at once, on the
//                        user's other phones through the server
//
// In a browser only the last part runs: a direct message seen on the desktop
// takes its notification off the phone. Without GhMessages nothing native is
// called.

const SEEN_DELAY_MS = 1000;           // a burst of reads reaches the server as one
const CATCH_UP_INTERVAL_MS = 30_000;
const UNREGISTER_TIMEOUT_MS = 2500;   // sign-out never waits longer on the network

let started = false;
let device = null;                    // this phone, as GhMessages last registered it
let lastCatchUp = 0;
const seenTimers = new Map();

function messagesPlugin() {
    return window.Capacitor?.Plugins?.GhMessages || null;
}

async function request(method, path, { body, signedIn = true, keepalive = false } = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if(signedIn) headers.Authorization = `Bearer ${await gudhub.getToken()}`;

    const response = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/push${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        keepalive
    });
    if(!response.ok) throw new Error(`${method} ${path} answered ${response.status}`);
    return response.json();
}

function warn(what, err) {
    console.warn(`[message-push] ${what}:`, err?.message || err);
}

//********************* INIT ********************************//
// Called once the messenger knows its user (pushNotificationHandler). Returns
// true inside the mobile shells, where notifications are native and the
// browser's Web Push must stay off.
// * messenger (HTMLElement) — the <gudhub-messenger> element
export function initMessagePush(messenger) {
    const messages = messagesPlugin();
    if(!GUDHUB_SETTINGS.conversation_server_url) return false;
    if(started) return Boolean(messages);
    started = true;

    trackSeen(messenger);
    if(!messages) return false;

    messages.addListener('notificationOpened', tap => openConversation(messenger, tap));
    messages.addListener('permissionChanged', () => register(messenger));
    document.addEventListener('visibilitychange', () => {
        if(document.visibilityState === 'visible') catchUp();
    });

    register(messenger);
    return true;
}

//********************* STOP ********************************//
// Sign-out (core/nativeShell.js): the phone stops showing this account's
// messages at once, and asks the server to stop pushing them. That needs no
// session - the phone proves itself by its install id and token - so it works
// just as well after the session was rejected. Never throws, and never holds
// the sign-out up longer than UNREGISTER_TIMEOUT_MS.
export async function stopMessagePush() {
    const messages = messagesPlugin();
    if(!messages) return;

    try {
        const { installationId, token } = await messages.unregister();
        device = null;
        if(!GUDHUB_SETTINGS.conversation_server_url || !installationId || !token) return;

        await Promise.race([
            request('POST', '/devices/unregister', { body: { installation_id: installationId, token }, signedIn: false, keepalive: true }),
            new Promise(resolve => setTimeout(resolve, UNREGISTER_TIMEOUT_MS))
        ]);
    } catch (err) {
        warn('unregister', err);
    }
}

//********************* REGISTER ********************************//
// Binds the phone's notifications to this user and files the phone with the
// conversation server - or takes it off when it cannot show a notification: on Android
// an undelivered high-priority push teaches FCM to hold this app's pushes
// back, incoming calls' included. Runs again whenever that permission changes.
async function register(messenger) {
    const messages = messagesPlugin();

    try {
        device = await messages.register({ userId: String(messenger.activeUserId) });

        if(device.permission !== 'granted') {
            await request('POST', '/devices/unregister', { body: { installation_id: device.installationId, token: device.token }, signedIn: false });
            return;
        }

        await request('POST', '/devices', {
            body: {
                installation_id: device.installationId,
                platform: device.platform,
                token: device.token,
                environment: device.environment || null,
                locale: device.locale || null
            }
        });
        catchUp();
    } catch (err) {
        warn('register', err);
    }
}

//********************* OPEN CONVERSATION ********************************//
// A tapped notification - also the one that launched the app, delivered once
// this listener exists. Never one addressed to another account.
function openConversation(messenger, { conversationId, conversationType, senderId, recipientId } = {}) {
    if(!conversationId || String(recipientId) !== String(messenger.activeUserId)) return;

    if(conversationType === 'direct' && senderId) {
        messenger.setExpanded(true);
        messenger.dispatchEvent(new CustomEvent('switch_chat_view', {
            bubbles: false,
            detail: { view: 'direct_message', user_id: String(senderId) }
        }));
        return;
    }

    // Anything else is resolved the way a Web Push click is (pushNotificationHandler).
    window.dispatchEvent(new CustomEvent('gh_push_notification_click', { detail: { conversation_id: conversationId } }));
}

//********************* SEEN ********************************//
// A direct message counts as seen once its conversation is on screen: the page
// visible, the panel open, the chat showing that conversation. That - not the
// conversation server's read cursor - is what takes its notifications away,
// since the chat also marks messages read while the app sits in the
// background, where nobody has seen them.
function trackSeen(messenger) {
    const chat = messenger.querySelector('gh-direct-message-chat');
    let shown = null;

    const update = (read = false) => {
        const conversationId = visibleDirectConversation(messenger, chat);
        const changed = conversationId !== shown;
        shown = conversationId;

        if(changed) {
            messagesPlugin()?.setActiveConversation({ conversationId }).catch(err => warn('active conversation', err));
        }
        if(conversationId && (changed || read)) seen(conversationId);
    };

    const observer = new MutationObserver(() => update());
    observer.observe(messenger, { attributes: true, attributeFilter: ['class'] });
    if(chat) observer.observe(chat, { attributes: true, attributeFilter: ['class'] });

    // Raised when the chat opens a conversation, and when a message arrives in
    // the open one (dataFetchMixin's markAsRead).
    messenger.addEventListener('conversation_read', () => update(true));
    document.addEventListener('visibilitychange', () => update());

    update();
}

function visibleDirectConversation(messenger, chat) {
    const onScreen = chat
        && document.visibilityState === 'visible'
        && messenger.classList.contains('conversations--expanded')
        && (!messenger.isMobile() || messenger.classList.contains('conversations--chat-active'))
        && !chat.classList.contains('hidden')
        && chat.viewMode === 'dm';

    return onScreen ? chat.conversationId || null : null;
}

function seen(conversationId) {
    messagesPlugin()?.clearConversation({ conversationId }).catch(err => warn('clear', err));

    clearTimeout(seenTimers.get(conversationId));
    seenTimers.set(conversationId, setTimeout(() => {
        seenTimers.delete(conversationId);
        request('POST', '/seen', { body: { conversation_id: conversationId, installation_id: device?.installationId || null } })
            .then(({ badge }) => messagesPlugin()?.setBadge({ count: badge }))
            .catch(err => warn('read', err));
    }, SEEN_DELAY_MS));
}

//********************* CATCH UP ********************************//
// Each time the app comes back: what was seen elsewhere while this phone was
// not told - iOS delivers the server's "read" pushes only when it chooses to,
// and never to an app the user swiped away - leaves the notification center,
// and the badge is set from the server's count.
async function catchUp() {
    const messages = messagesPlugin();
    if(device?.permission !== 'granted' || Date.now() - lastCatchUp < CATCH_UP_INTERVAL_MS) return;
    lastCatchUp = Date.now();

    try {
        const since = Date.now();
        const { conversation_ids: conversationIds, badge } = await request('GET', '/pending');
        await messages.syncPending({ conversationIds, since });
        await messages.setBadge({ count: badge });
    } catch (err) {
        warn('catch up', err);
    }
}
